import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { initialAIConversationsState } from 'slices/aiConversations/initialState'
import {
  AIConversation,
  AIConversationsState,
  Message,
} from 'slices/aiConversations/types'
import { saveSegmentThunk } from './thunks'

const aiConversationsSlice = createSlice({
  name: 'aiConversations',
  initialState: initialAIConversationsState as AIConversationsState,
  reducers: {
    setActiveConversation: (state, action: PayloadAction<AIConversation>) => {
      state.activeConversation = action.payload
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      if (state.activeConversation) {
        state.activeConversation.messages.push(action.payload)
      }
    },
    updateMessages: (state, action: PayloadAction<Message[]>) => {
      if (state.activeConversation) {
        state.activeConversation.messages = action.payload
      }
    },
    addSuggestionMessage: (state, action: PayloadAction<string>) => {
      if (state.activeConversation) {
        const message: Message = {
          id: `suggestion-${Date.now()}`,
          content: action.payload,
          type: 'user',
          timestamp: new Date(),
          sequenceNumber: state.activeConversation.messages.length + 1,
          isOptimistic: true,
        }
        state.activeConversation.messages.push(message)
      }
    },
    addToolCallMessage: (state, action: PayloadAction<string>) => {
      if (state.activeConversation) {
        const message: Message = {
          id: `tool-call-${Date.now()}`,
          content: action.payload,
          type: 'tool_call',
          timestamp: new Date(),
          sequenceNumber: state.activeConversation.messages.length + 1,
          isToolCall: true,
        }
        state.activeConversation.messages.push(message)
      }
    },
    updateLastMessage: (state, action: PayloadAction<Partial<Message>>) => {
      if (
        state.activeConversation &&
        state.activeConversation.messages.length > 0
      ) {
        const lastIndex = state.activeConversation.messages.length - 1
        state.activeConversation.messages[lastIndex] = {
          ...state.activeConversation.messages[lastIndex],
          ...action.payload,
        }
      }
    },
    setPickerSubmitted: (
      state,
      action: PayloadAction<{ messageId: string; submitted: boolean }>
    ) => {
      if (state.activeConversation) {
        const message = state.activeConversation.messages.find(
          m => m.id === action.payload.messageId
        )
        if (message) {
          message.pickerSubmitted = action.payload.submitted
        }
      }
    },
    // WebSocket reducers
    setConnectionStatus: (
      state,
      action: PayloadAction<
        'disconnected' | 'connecting' | 'connected' | 'error'
      >
    ) => {
      state.webSocket.connectionStatus = action.payload
      if (action.payload === 'connected') {
        state.webSocket.connectionError = null
      }
    },
    setConnectionError: (state, action: PayloadAction<string | null>) => {
      state.webSocket.connectionError = action.payload
      if (action.payload) {
        state.webSocket.connectionStatus = 'error'
      }
    },
    setIsProcessing: (state, action: PayloadAction<boolean>) => {
      state.webSocket.isProcessing = action.payload
    },
    setManagerId: (state, action: PayloadAction<string | null>) => {
      state.webSocket.managerId = action.payload
    },
    setCurrentStreamingMessage: (
      state,
      action: PayloadAction<Message | null>
    ) => {
      state.webSocket.currentStreamingMessage = action.payload
    },
    setAwaitingResponse: (state, action: PayloadAction<boolean>) => {
      state.webSocket.awaitingResponse = action.payload
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.webSocket.isLoading = action.payload
    },
    setChatId: (state, action: PayloadAction<string | null>) => {
      state.webSocket.chatId = action.payload
    },
  },
  extraReducers: builder => {
    builder
      // Handle saveSegmentThunk lifecycle
      .addCase(saveSegmentThunk.pending, state => {
        // Thunk started - save operation in progress
        // Note: setSegmentSaving(true) is handled by component before dispatching thunk
        state.webSocket.isProcessing = true
      })
      .addCase(saveSegmentThunk.fulfilled, (state, _action) => {
        // Thunk completed successfully
        // Note: The actual save completion and setSegmentSaving(false) will be handled
        // by the SEGMENT_DATA message handler when WebSocket response arrives
        state.webSocket.isProcessing = false
      })
      .addCase(saveSegmentThunk.rejected, (state, action) => {
        // Thunk failed - reset save state and show error
        state.webSocket.isProcessing = false
        state.webSocket.connectionError =
          (action.payload as string) || 'Failed to save segment'
      })
  },
})

export const {
  setActiveConversation,
  addMessage,
  updateMessages,
  addSuggestionMessage,
  addToolCallMessage,
  updateLastMessage,
  setPickerSubmitted,
  setConnectionStatus,
  setConnectionError,
  setIsProcessing,
  setManagerId,
  setCurrentStreamingMessage,
  setAwaitingResponse,
  setIsLoading,
  setChatId,
} = aiConversationsSlice.actions

export const reducer = aiConversationsSlice.reducer
export const selectAIConversations = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations

// WebSocket selectors
export const selectWebSocketState = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket

export const selectConnectionStatus = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.connectionStatus

export const selectIsWebSocketConnected = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.connectionStatus === 'connected'

export const selectWebSocketError = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.connectionError

export const selectIsWebSocketProcessing = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.isProcessing

export const selectWebSocketManagerId = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.managerId

export const selectCurrentStreamingMessage = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.currentStreamingMessage

export const selectAwaitingResponse = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.awaitingResponse

export const selectIsLoading = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.isLoading

export const selectChatId = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.webSocket.chatId

// Message selectors
export const selectMessages = (state: {
  aiConversations: AIConversationsState
}) => state.aiConversations.activeConversation?.messages || []

export const selectLastMessage = (state: {
  aiConversations: AIConversationsState
}) => {
  const messages = state.aiConversations.activeConversation?.messages || []
  return messages.length > 0 ? messages[messages.length - 1] : null
}
